import {
    collection,
    doc,
    addDoc,
    getDocs,
    query,
    orderBy,
    where,
    serverTimestamp,
    Timestamp,
    updateDoc,
    onSnapshot,
    QuerySnapshot,
    DocumentData,
    increment
} from 'firebase/firestore';
import { db } from '../firebase/firebase.config';

// Visa types enum for type safety
export enum VisaType {
    GLOBAL_TALENT = 'global-talent-visa',
    TOURIST = 'tourist-visa'
}

// Message sender types
export enum MessageSender {
    USER = 'user',
    ASSISTANT = 'assistant'
}

// Message status for server processing
export enum MessageStatus {
    SENT = 'sent',
    PROCESSING = 'processing',
    DELIVERED = 'delivered',
    ERROR = 'error'
}

// Core message interface (simplified since chatId and userId are inherited from parent)
export interface ChatMessage {
    id?: string;
    sender: MessageSender;
    content: string;
    timestamp: Timestamp;
    status: MessageStatus;
    // Optional metadata for future features
    metadata?: {
        attachments?: string[];
        replyTo?: string;
        edited?: boolean;
        editedAt?: Timestamp;
    };
}

// Chat session interface
export interface ChatSession {
    id?: string;
    userId: string;
    visaType: VisaType;
    title: string;
    createdAt: Timestamp;
    updatedAt: Timestamp;
    lastMessage?: {
        content: string;
        timestamp: Timestamp;
        sender: MessageSender;
    };
    messageCount: number;
    isActive: boolean;
    // Metadata for chat management
    metadata?: {
        tags?: string[];
        priority?: 'low' | 'medium' | 'high';
        archived?: boolean;
    };
}

// Create a new chat session
export const createChatSession = async (
    userId: string,
    visaType: VisaType,
    title?: string
): Promise<string> => {
    try {
        const chatData: Omit<ChatSession, 'id'> = {
            userId,
            visaType,
            title: title || `${visaType.replace('-', ' ')} Chat`,
            createdAt: serverTimestamp() as Timestamp,
            updatedAt: serverTimestamp() as Timestamp,
            messageCount: 0,
            isActive: true
        };

        const chatRef = await addDoc(collection(db, 'chats'), chatData);
        return chatRef.id;
    } catch (error) {
        console.error('Error creating chat session:', error);
        throw error;
    }
};

// Send a message (user message)
export const sendMessage = async (
    chatId: string,
    userId: string,
    content: string,
    _visaType: VisaType
): Promise<string> => {
    try {
        const messageData: Omit<ChatMessage, 'id'> = {
            sender: MessageSender.USER,
            content,
            timestamp: serverTimestamp() as Timestamp,
            status: MessageStatus.SENT
        };

        // Add the message to the chat's messages subcollection
        const messagesRef = collection(db, 'chats', chatId, 'messages');
        const messageRef = await addDoc(messagesRef, messageData);

        // Update chat session with atomic increment
        await updateDoc(doc(db, 'chats', chatId), {
            lastMessage: {
                content,
                timestamp: serverTimestamp(),
                sender: MessageSender.USER
            },
            updatedAt: serverTimestamp(),
            messageCount: increment(1)
        });

        return messageRef.id;
    } catch (error) {
        console.error('Error sending message:', error);
        throw error;
    }
};

// Add assistant message (for server API integration)
export const addAssistantMessage = async (
    chatId: string,
    userId: string,
    content: string,
    _visaType: VisaType,
    status: MessageStatus = MessageStatus.DELIVERED
): Promise<string> => {
    try {
        const messageData: Omit<ChatMessage, 'id'> = {
            sender: MessageSender.ASSISTANT,
            content,
            timestamp: serverTimestamp() as Timestamp,
            status
        };

        // Add the message to the chat's messages subcollection
        const messagesRef = collection(db, 'chats', chatId, 'messages');
        const messageRef = await addDoc(messagesRef, messageData);

        // Update chat session with atomic increment
        await updateDoc(doc(db, 'chats', chatId), {
            lastMessage: {
                content,
                timestamp: serverTimestamp(),
                sender: MessageSender.ASSISTANT
            },
            updatedAt: serverTimestamp(),
            messageCount: increment(1)
        });

        return messageRef.id;
    } catch (error) {
        console.error('Error adding assistant message:', error);
        throw error;
    }
};

// Get messages for a chat session
export const getChatMessages = async (chatId: string): Promise<ChatMessage[]> => {
    try {
        const messagesRef = collection(db, 'chats', chatId, 'messages');
        const messagesQuery = query(messagesRef, orderBy('timestamp', 'asc'));

        const querySnapshot = await getDocs(messagesQuery);
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as ChatMessage));
    } catch (error) {
        console.error('Error fetching chat messages:', error);
        throw error;
    }
};

// Real-time message listener
export const subscribeToChatMessages = (
    chatId: string,
    callback: (messages: ChatMessage[]) => void
): (() => void) => {
    const messagesRef = collection(db, 'chats', chatId, 'messages');
    const messagesQuery = query(messagesRef, orderBy('timestamp', 'asc'));

    return onSnapshot(messagesQuery, (snapshot: QuerySnapshot<DocumentData>) => {
        const messages = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as ChatMessage));
        callback(messages);
    });
};

// Get user's chat sessions for a specific visa type
export const getUserChatSessions = async (
    userId: string,
    visaType?: VisaType
): Promise<ChatSession[]> => {
    try {
        let chatQuery = query(
            collection(db, 'chats'),
            where('userId', '==', userId),
            orderBy('updatedAt', 'desc')
        );

        if (visaType) {
            chatQuery = query(
                collection(db, 'chats'),
                where('userId', '==', userId),
                where('visaType', '==', visaType),
                orderBy('updatedAt', 'desc')
            );
        }

        const querySnapshot = await getDocs(chatQuery);
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as ChatSession));
    } catch (error) {
        console.error('Error fetching chat sessions:', error);
        throw error;
    }
};

// Update chat session
export const updateChatSession = async (
    chatId: string,
    updates: Partial<ChatSession>
): Promise<void> => {
    try {
        await updateDoc(doc(db, 'chats', chatId), {
            ...updates,
            updatedAt: serverTimestamp()
        });
    } catch (error) {
        console.error('Error updating chat session:', error);
        throw error;
    }
};

// Update message status (useful for server processing)
export const updateMessageStatus = async (
    chatId: string,
    messageId: string,
    status: MessageStatus
): Promise<void> => {
    try {
        const messageRef = doc(db, 'chats', chatId, 'messages', messageId);
        await updateDoc(messageRef, {
            status,
            'metadata.editedAt': serverTimestamp()
        });
    } catch (error) {
        console.error('Error updating message status:', error);
        throw error;
    }
};

// Archive/delete chat session
export const archiveChatSession = async (chatId: string): Promise<void> => {
    try {
        await updateChatSession(chatId, {
            isActive: false,
            metadata: {
                archived: true
            }
        });
    } catch (error) {
        console.error('Error archiving chat session:', error);
        throw error;
    }
};

// Search messages within a specific chat (for future features)
export const searchChatMessages = async (
    chatId: string,
    searchTerm: string
): Promise<ChatMessage[]> => {
    try {
        const messagesRef = collection(db, 'chats', chatId, 'messages');
        const messagesQuery = query(messagesRef, orderBy('timestamp', 'desc'));

        const querySnapshot = await getDocs(messagesQuery);
        const messages = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as ChatMessage));

        // Client-side filtering (consider server-side for production)
        return messages.filter(message =>
            message.content.toLowerCase().includes(searchTerm.toLowerCase())
        );
    } catch (error) {
        console.error('Error searching chat messages:', error);
        throw error;
    }
};

// Search messages across all user's chats
export const searchAllUserMessages = async (
    userId: string,
    searchTerm: string,
    visaType?: VisaType
): Promise<{ chatId: string; message: ChatMessage }[]> => {
    try {
        // First get all user's chat sessions
        const chatSessions = await getUserChatSessions(userId, visaType);
        const results: { chatId: string; message: ChatMessage }[] = [];

        // Search through each chat's messages
        for (const chat of chatSessions) {
            if (chat.id) {
                const messages = await searchChatMessages(chat.id, searchTerm);
                messages.forEach(message => {
                    results.push({ chatId: chat.id!, message });
                });
            }
        }

        // Sort by timestamp (most recent first)
        return results.sort((a, b) => {
            const aTime = a.message.timestamp?.toDate?.() || new Date(0);
            const bTime = b.message.timestamp?.toDate?.() || new Date(0);
            return bTime.getTime() - aTime.getTime();
        });
    } catch (error) {
        console.error('Error searching all user messages:', error);
        throw error;
    }
};

// Get chat session with message count (useful for dashboard)
export const getChatSessionWithStats = async (chatId: string): Promise<ChatSession | null> => {
    try {
        const chatDoc = await getDocs(query(collection(db, 'chats'), where('__name__', '==', chatId)));
        if (chatDoc.empty) return null;

        const chatData = chatDoc.docs[0].data() as ChatSession;

        // Get actual message count from subcollection
        const messagesRef = collection(db, 'chats', chatId, 'messages');
        const messageSnapshot = await getDocs(messagesRef);

        return {
            id: chatDoc.docs[0].id,
            ...chatData,
            messageCount: messageSnapshot.size
        };
    } catch (error) {
        console.error('Error fetching chat session with stats:', error);
        throw error;
    }
}; 